import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
  Typography,
  createFilterOptions
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { searchAssessmentsByInstrumentAndSponsor } from "./catalogAPI";
import { compactFieldSx } from "./formStyles";

export type AssessmentSuggestion = {
  id: string;
  title: string;
  description: string | null;
  requireTraineeSelfAssessment: boolean | null;
  creationDurationHours: number | null;
  expirationDurationHours: number | null;
};

type Props = {
  /** Required to scope the search; both must be set for the field to be usable. */
  sourceInstrumentId?: string;
  sponsorId?: string;
  value: string;
  required?: boolean;
  /**
   * Called whenever the title text changes. `match` is the existing assessment
   * row when the typed/selected title matches verbatim (case-insensitive trim).
   */
  onChange: (next: { title: string; match: AssessmentSuggestion | null }) => void;
  sx?: object;
};

const filterOptions = createFilterOptions<AssessmentSuggestion>({
  matchFrom: "any",
  stringify: (opt) => `${opt.title} ${opt.description ?? ""}`
});

export function AssessmentTitleAutocomplete({
  sourceInstrumentId,
  sponsorId,
  value,
  required,
  onChange,
  sx
}: Props) {
  const [inputValue, setInputValue] = useState(value);
  const [options, setOptions] = useState<AssessmentSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The DB-backed dropdown only makes sense when both scoping ids are set —
  // we need an existing instrument id to query assessments under, plus the
  // sponsor for the chosen network. Without those, we can't *list* anything,
  // but the user must still be able to TYPE a brand-new assessment title for
  // an instrument that's being created from scratch. So the dropdown can be
  // empty while the input itself stays editable.
  const dropdownEnabled = Boolean(sourceInstrumentId && sponsorId);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Load every assessment for (instrument, sponsor) the moment scoping is
  // available. Filtering happens client-side. The list per scope is
  // typically small (handful at most), so server-side debouncing isn't
  // worth the round-trip per keystroke.
  useEffect(() => {
    if (!dropdownEnabled || !sourceInstrumentId || !sponsorId) {
      setOptions([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    searchAssessmentsByInstrumentAndSponsor(sourceInstrumentId, sponsorId)
      .then((res) => {
        if (cancelled) return;
        const opts: AssessmentSuggestion[] = (res.assessments ?? []).map((a) => ({
          id: a.id,
          title: a.title,
          description: a.description ?? null,
          requireTraineeSelfAssessment: a.requireTraineeSelfAssessment ?? null,
          creationDurationHours: a.creationDurationHours ?? null,
          expirationDurationHours: a.expirationDurationHours ?? null
        }));
        setOptions(opts);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Search failed.");
        setOptions([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dropdownEnabled, sourceInstrumentId, sponsorId]);

  const exactMatch = useMemo<AssessmentSuggestion | null>(() => {
    const trimmedLower = inputValue.trim().toLowerCase();
    if (!trimmedLower) return null;
    return (
      options.find((o) => o.title.trim().toLowerCase() === trimmedLower) ?? null
    );
  }, [inputValue, options]);

  useEffect(() => {
    onChange({ title: inputValue, match: exactMatch });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue, exactMatch?.id]);

  return (
    <Box sx={sx}>
      <Autocomplete
        size="small"
        freeSolo
        clearOnBlur={false}
        selectOnFocus
        disabled={!sponsorId}
        options={options}
        loading={loading}
        filterOptions={filterOptions}
        getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt.title)}
        isOptionEqualToValue={(a, b) =>
          (typeof a === "string" ? a : a.id) === (typeof b === "string" ? b : b.id)
        }
        inputValue={inputValue}
        onInputChange={(_, val) => setInputValue(val)}
        onChange={(_, val) => {
          if (val && typeof val === "object") {
            setInputValue(val.title);
          }
        }}
        sx={compactFieldSx}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Assessment title"
            required={required}
            placeholder={
              !sponsorId
                ? "Pick a target network first"
                : dropdownEnabled
                  ? "Search existing or type new"
                  : "Type assessment title"
            }
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={14} /> : null}
                  {params.InputProps.endAdornment}
                </>
              )
            }}
          />
        )}
        renderOption={(props, opt) => (
          <Box component="li" {...props} key={opt.id}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {opt.title}
              </Typography>
              {opt.description ? (
                <Typography variant="caption" color="text.secondary">
                  {opt.description}
                </Typography>
              ) : null}
            </Box>
          </Box>
        )}
      />
      {error ? (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      ) : null}
    </Box>
  );
}
